import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import analyticsService, { type GlobalSearchResult } from '@services/analytics.service';

const ROUTES: Record<GlobalSearchResult['type'], (id: string) => string> = {
  customer: (id) => `/settings/customers/${id}`,
  vehicle: (id) => `/fleet/${id}`,
  driver: (id) => `/fleet?tab=drivers`,
  docket: (id) => `/dockets/${id}`,
  shipment: (id) => `/dockets`,
  invoice: (id) => `/billing/${id}`,
};

export const GlobalSearch: React.FC = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<GlobalSearchResult[]>([]);

  const runSearch = useCallback(async (term: string) => {
    if (!term.trim()) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const res = await analyticsService.globalSearch(term.trim());
      setResults(res.results);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => void runSearch(q), 300);
    return () => window.clearTimeout(t);
  }, [q, runSearch]);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex w-full items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/80 transition hover:bg-white/10"
        aria-label="Open global search"
      >
        <Search className="h-4 w-4 shrink-0" />
        <span className="truncate">Search…</span>
      </button>
    );
  }

  return (
    <div className="relative">
      <div className="flex items-center gap-2 rounded-xl border border-white/15 bg-white/10 px-3 py-2">
        <Search className="h-4 w-4 shrink-0 text-white/70" />
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Customers, dockets, fleet…"
          className="min-w-0 flex-1 bg-transparent text-sm text-white placeholder:text-white/50 focus:outline-none"
        />
        <button type="button" onClick={() => { setOpen(false); setQ(''); setResults([]); }} aria-label="Close search">
          <X className="h-4 w-4 text-white/70" />
        </button>
      </div>
      {(loading || results.length > 0 || q.trim()) && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 max-h-72 overflow-y-auto rounded-xl border border-neutral-200 bg-white shadow-xl">
          {loading && <p className="px-4 py-3 text-sm text-neutral-500">Searching…</p>}
          {!loading && results.length === 0 && q.trim() && (
            <p className="px-4 py-3 text-sm text-neutral-500">No results</p>
          )}
          {results.map((r) => (
            <button
              key={`${r.type}-${r.id}`}
              type="button"
              className="flex w-full flex-col items-start gap-0.5 border-b border-neutral-50 px-4 py-3 text-left hover:bg-neutral-50"
              onClick={() => {
                navigate(ROUTES[r.type](r.id));
                setOpen(false);
                setQ('');
                setResults([]);
              }}
            >
              <span className="text-xs font-semibold uppercase tracking-wide text-neutral-400">{r.type}</span>
              <span className="font-medium text-neutral-900">{r.label}</span>
              {r.meta && <span className="text-xs text-neutral-500">{r.meta}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
